// Join-request toast (design "join-request toast"): the newest pending knock
// as a bottom-left card with Admit/Deny. The full queue lives in the People
// panel; the toast surfaces the head of it so the host never misses a knock.

import { memo, useCallback } from 'react';

import { api } from '../api';
import { probeRender } from '../perf/renderProbe';
import { pushToast, removeKnock, useCallStore } from '../store/callStore';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';

export const KnockToast = memo(function KnockToast({ slug }: { slug: string }) {
  probeRender('KnockToast');
  const knock = useCallStore((s) => s.knocks[0] ?? null);

  const decide = useCallback(
    (action: 'admit' | 'deny') => {
      const k = knock;
      if (!k) return;
      removeKnock(k.id); // optimistic; WS knock_gone confirms
      void (action === 'admit' ? api.admit(slug, k.id) : api.deny(slug, k.id)).catch(() =>
        pushToast('Decision failed — the request may have expired', 'warning'),
      );
    },
    [slug, knock],
  );
  const admit = useCallback(() => decide('admit'), [decide]);
  const deny = useCallback(() => decide('deny'), [decide]);

  if (!knock) return null;

  return (
    <div className="bz-knock-toast" role="alert">
      <Avatar name={knock.name} identity={knock.id} size={36} />
      <div className="bz-knock-toast-main">
        <div className="bz-knock-toast-name">{knock.name}</div>
        <div className="bz-knock-sub">wants to join this meeting</div>
      </div>
      <Button size="sm" onClick={deny}>
        Deny
      </Button>
      <Button size="sm" variant="primary" onClick={admit}>
        Admit
      </Button>
    </div>
  );
});
